import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setToastContextGetter } from '@/hooks/ui/use-toast';
import { registerDashboardTabSetter, unregisterDashboardTabSetter } from './ShowUserContext';
import { DASHBOARD_ROUTES, DEFAULT_TABS, isTabAllowedForPlan, type DashboardType } from '@/lib/constants/dashboard';
import { useSubscription } from '@/context/SubscriptionContext';

interface HighlightedItem {
  type: 'shift' | 'employee' | 'review';
  id: string;
}

interface DashboardTabContextProps {
  businessDashboardTab: string;
  employeeDashboardTab: string;
  employeePortalTab: string;
  franchiseDashboardTab: string;
  selectedShiftDate: Date | null;
  selectedBusinessName: string | null;
  highlightedItem: HighlightedItem | null;
  navigateToShiftMonth: Date | null;
  shiftToSelect: string | null; // Shift ID to select for editing when navigating
  employeeRoleFilter: string | null; // Filter employees by role ID
  setBusinessDashboardTab: (tab: string) => void;
  setEmployeeDashboardTab: (tab: string) => void;
  setEmployeePortalTab: (tab: string) => void;
  setFranchiseDashboardTab: (tab: string) => void;
  setSelectedShiftDate: (date: Date | null) => void;
  setSelectedBusinessName: (name: string | null) => void;
  setHighlightedItem: (item: HighlightedItem | null) => void;
  setNavigateToShiftMonth: (date: Date | null) => void;
  setShiftToSelect: (shiftId: string | null) => void;
  setEmployeeRoleFilter: (roleId: string | null) => void;
}

const DashboardTabContext = createContext<DashboardTabContextProps | undefined>(undefined);

interface DashboardTabProviderProps {
  children: ReactNode;
}

interface SyncedTabConfig {
  route: string;
  storageKey: string;
  defaultTab: string;
}

function isOnRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(route + '/');
}

/**
 * Internal hook: syncs a tab state with URL ?tab= param and localStorage.
 * - When on the matching route, URL ?tab= is the source of truth.
 * - When off-route, state/localStorage holds the value.
 * - Setter always updates state + localStorage. If on-route, also updates URL (replace).
 * - Effect 1: URL → state sync (browser back/forward).
 * - Effect 2: inject ?tab= from state when arriving at route without one.
 */
function useSyncedTabState(config: SyncedTabConfig): [string, (tab: string) => void] {
  const { route, storageKey, defaultTab } = config;
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize from URL (direct entry) → localStorage → default
  const [tab, setTabState] = useState<string>(() => {
    if (isOnRoute(window.location.pathname, route)) {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get('tab');
      if (urlTab) {
        try { localStorage.setItem(storageKey, urlTab); } catch {}
        return urlTab;
      }
    }
    try {
      return localStorage.getItem(storageKey) || defaultTab;
    } catch {
      return defaultTab;
    }
  });

  const onRoute = isOnRoute(location.pathname, route);

  // Effect 1: URL → state sync (handles browser back/forward)
  useEffect(() => {
    if (!onRoute) return;
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');
    if (urlTab && urlTab !== tab) {
      setTabState(urlTab);
      try { localStorage.setItem(storageKey, urlTab); } catch {}
    }
  }, [onRoute, location.search, storageKey]); // intentionally exclude `tab` — we only sync URL → state

  // Effect 2: inject ?tab= when arriving at route without one
  useEffect(() => {
    if (!onRoute) return;
    const params = new URLSearchParams(location.search);
    if (!params.get('tab')) {
      params.set('tab', tab);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [onRoute, location.pathname]); // intentionally minimal deps — only fire on route arrival

  const setter = useCallback((newTab: string) => {
    // Always update state + localStorage
    setTabState(newTab);
    try { localStorage.setItem(storageKey, newTab); } catch {}

    // If currently on the route, also update URL via navigate (keeps React Router in sync)
    const currentPath = window.location.pathname;
    if (isOnRoute(currentPath, route)) {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', newTab);
      navigate({ pathname: currentPath, search: params.toString() }, { replace: true });
    }
  }, [storageKey, route, navigate]);

  return [tab, setter];
}

export function DashboardTabProvider({ children }: DashboardTabProviderProps) {
  const { currentPlan } = useSubscription();
  const planName = currentPlan?.name;

  const [businessDashboardTab, setBusinessDashboardTab] = useSyncedTabState({
    route: DASHBOARD_ROUTES.business,
    storageKey: 'business-dashboard-tab',
    defaultTab: DEFAULT_TABS.business,
  });

  const [employeeDashboardTab, setEmployeeDashboardTab] = useSyncedTabState({
    route: DASHBOARD_ROUTES.employee,
    storageKey: 'employee-dashboard-tab',
    defaultTab: DEFAULT_TABS.employee,
  });

  const [employeePortalTab, setEmployeePortalTab] = useSyncedTabState({
    route: DASHBOARD_ROUTES['employee-portal'],
    storageKey: 'employee-portal-tab',
    defaultTab: DEFAULT_TABS['employee-portal'],
  });

  const [franchiseDashboardTab, setFranchiseDashboardTab] = useSyncedTabState({
    route: DASHBOARD_ROUTES.franchise,
    storageKey: 'franchise-dashboard-tab',
    defaultTab: DEFAULT_TABS.franchise,
  });

  const [selectedShiftDate, setSelectedShiftDateState] = useState<Date | null>(() => {
    try {
      const storedDate = localStorage.getItem('selected-shift-date');
      return storedDate ? new Date(storedDate) : null;
    } catch (e) {
      return null;
    }
  });

  const [selectedBusinessName, setSelectedBusinessNameState] = useState<string | null>(null);
  const [highlightedItem, setHighlightedItemState] = useState<HighlightedItem | null>(null);
  const [navigateToShiftMonth, setNavigateToShiftMonthState] = useState<Date | null>(null);
  const [shiftToSelect, setShiftToSelectState] = useState<string | null>(null);
  const [employeeRoleFilter, setEmployeeRoleFilterState] = useState<string | null>(null);

  // Auto-clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedItem) {
      const timer = setTimeout(() => setHighlightedItemState(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedItem]);

  const guardTab = useCallback((dashboard: DashboardType, tab: string) => {
    return isTabAllowedForPlan(planName, dashboard, tab) ? tab : DEFAULT_TABS[dashboard];
  }, [planName]);

  const setGuardedBusinessDashboardTab = useCallback((tab: string) => {
    setBusinessDashboardTab(guardTab('business', tab));
  }, [guardTab, setBusinessDashboardTab]);

  const setGuardedEmployeeDashboardTab = useCallback((tab: string) => {
    setEmployeeDashboardTab(guardTab('employee', tab));
  }, [guardTab, setEmployeeDashboardTab]);

  const setGuardedFranchiseDashboardTab = useCallback((tab: string) => {
    setFranchiseDashboardTab(guardTab('franchise', tab));
  }, [guardTab, setFranchiseDashboardTab]);

  // A pasted ?tab= is applied inside useSyncedTabState. Pull a free plan back to the default.
  useEffect(() => {
    if (planName !== 'free') return;
    if (businessDashboardTab !== guardTab('business', businessDashboardTab)) {
      setBusinessDashboardTab(DEFAULT_TABS.business);
    }
    if (employeeDashboardTab !== guardTab('employee', employeeDashboardTab)) {
      setEmployeeDashboardTab(DEFAULT_TABS.employee);
    }
    if (franchiseDashboardTab !== guardTab('franchise', franchiseDashboardTab)) {
      setFranchiseDashboardTab(DEFAULT_TABS.franchise);
    }
  }, [
    planName,
    businessDashboardTab,
    employeeDashboardTab,
    franchiseDashboardTab,
    guardTab,
    setBusinessDashboardTab,
    setEmployeeDashboardTab,
    setFranchiseDashboardTab,
  ]);

  // Register tab setter for ShowUserContext (AI navigation)
  useEffect(() => {
    registerDashboardTabSetter(setGuardedBusinessDashboardTab);
    return () => unregisterDashboardTabSetter();
  }, [setGuardedBusinessDashboardTab]);

  const setSelectedShiftDate = useCallback((date: Date | null) => {
    try {
      if (date) {
        localStorage.setItem('selected-shift-date', date.toISOString());
      } else {
        localStorage.removeItem('selected-shift-date');
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    setSelectedShiftDateState(date);
  }, []);

  const setSelectedBusinessName = useCallback((name: string | null) => {
    setSelectedBusinessNameState(name);
  }, []);

  const setHighlightedItem = useCallback((item: HighlightedItem | null) => {
    setHighlightedItemState(item);
  }, []);

  const setNavigateToShiftMonth = useCallback((date: Date | null) => {
    setNavigateToShiftMonthState(date);
  }, []);

  const setShiftToSelect = useCallback((shiftId: string | null) => {
    setShiftToSelectState(shiftId);
  }, []);

  const setEmployeeRoleFilter = useCallback((roleId: string | null) => {
    setEmployeeRoleFilterState(roleId);
  }, []);

  // Set up toast context getter
  useEffect(() => {
    const getTabCategory = (tab: string): string => {
      const tabMap: Record<string, string> = {
        'insights': 'Dashboard',
        'reviews': 'Reviews',
        'loyalty': 'Loyalty Program',
        'wallet-statistics': 'Wallet Analytics',
        'coupons': 'Coupons',
        'bulk-mail': 'Bulk Mail',
        'email-templates': 'Email Templates',
        'employee-list': 'Employee Management',
        'shift-management': 'Shift Management',
        'time-tracking': 'Time Tracking',
        'payroll': 'Payroll',
        'financial': 'Financial',
        'analytics': 'Analytics',
        'communications': 'Communications',
        'meetings': 'Meetings',
        'signage': 'Digital Signage',
        'my-dashboard': 'My Dashboard',
        'my-shifts': 'My Shifts',
        'my-payroll': 'My Payroll',
      };
      return tabMap[tab] || tab;
    };

    setToastContextGetter(() => {
      // Get the current path from window.location instead of useLocation hook
      const path = window.location.pathname;
      let category = 'System';

      // Check if on settings pages first (URL-based detection)
      if (path.startsWith('/settings/account')) {
        category = 'Account Settings';
      } else if (path.startsWith('/settings/business')) {
        category = 'Business Settings';
      } else if (path.includes('/dashboard')) {
        category = getTabCategory(businessDashboardTab);
      } else if (path.includes('/employee-dashboard')) {
        category = getTabCategory(employeeDashboardTab);
      } else if (path.includes('/employee-portal')) {
        category = getTabCategory(employeePortalTab);
      } else if (path.includes('/settings')) {
        category = 'Settings';
      } else if (path.includes('/business')) {
        category = 'Business Management';
      } else if (path.includes('/auth') || path.includes('/sign')) {
        category = 'Authentication';
      }

      return {
        category,
        businessName: selectedBusinessName || undefined,
      };
    });
  }, [businessDashboardTab, employeeDashboardTab, employeePortalTab, selectedBusinessName]);

  const contextValue = useMemo(() => ({
    businessDashboardTab,
    employeeDashboardTab,
    employeePortalTab,
    franchiseDashboardTab,
    selectedShiftDate,
    selectedBusinessName,
    highlightedItem,
    navigateToShiftMonth,
    shiftToSelect,
    employeeRoleFilter,
    setBusinessDashboardTab: setGuardedBusinessDashboardTab,
    setEmployeeDashboardTab: setGuardedEmployeeDashboardTab,
    setEmployeePortalTab,
    setFranchiseDashboardTab: setGuardedFranchiseDashboardTab,
    setSelectedShiftDate,
    setSelectedBusinessName,
    setHighlightedItem,
    setNavigateToShiftMonth,
    setShiftToSelect,
    setEmployeeRoleFilter
  }), [
    businessDashboardTab,
    employeeDashboardTab,
    employeePortalTab,
    franchiseDashboardTab,
    selectedShiftDate,
    selectedBusinessName,
    highlightedItem,
    navigateToShiftMonth,
    shiftToSelect,
    employeeRoleFilter,
    setGuardedBusinessDashboardTab,
    setGuardedEmployeeDashboardTab,
    setEmployeePortalTab,
    setGuardedFranchiseDashboardTab,
    setSelectedShiftDate,
    setSelectedBusinessName,
    setHighlightedItem,
    setNavigateToShiftMonth,
    setShiftToSelect,
    setEmployeeRoleFilter
  ]);

  return (
    <DashboardTabContext.Provider value={contextValue}>
      {children}
    </DashboardTabContext.Provider>
  );
}

export const useDashboardTab = (): DashboardTabContextProps => {
  const context = useContext(DashboardTabContext);
  if (context === undefined) {
    throw new Error('useDashboardTab must be used within a DashboardTabProvider');
  }
  return context;
};

export { DashboardTabContext };
